/* ============================================================== */
/*                                                                */
/*   PatientListV4 sequence  ·  HIS V4 · Patient Queue             */
/*                                                                */
/*   1) เรียก API  GET /apis/core/encounter-patient-optimized/      */
/*   2) queue domain (ขั้น/สถานะคำสั่ง/เวลา) ของตารางคิว           */
/*   3) map แถว API -> QueueEncounter สำหรับ CardPatientListV4     */
/*                                                                */
/*   UI ทุกไฟล์ (CardPatientListV4, MainHISV4, PatientPanelV4)      */
/*   ควร import ผ่าน common/commonInterface.ts ไม่ใช่ไฟล์นี้ตรง ๆ  */
/*                                                                */
/* ============================================================== */

// framework เดียวกับ PatientOrdersV4 (team practice)
import moment from "moment";

import {
  ActionHandler,
  BaseSeqState,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  apiErrorTextOf,
  markButtonLoadCheck,
  seqBtnKey,
  writeSequenceState,
  type BLC_STATUS_TYPE,
  type SlicePatch,
} from "./sequenceStateV4";
import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";

import {
  NormalizeFilter,
  createdText,
  nextStageAfterExam,
  stageOf,
  toQueueRows,
  type FilterType,
  type QueueEncounter,
  type UiFilterType,
} from "../queueDomainV4";

// APIs
import EncounterPatientListOptimized from "issara-sdk/apis/EncounterPatientListOptimized_core";
import EncounterActionOnly from "issara-sdk/apis/EncounterActionOnly_core";
import ClassifiedPatientHistoryList from "issara-sdk/apis/ClassifiedPatientHistoryList_apps_DPO";
import { decodeHtml } from "../../patient/examModelV4";

/* ============================================================== */
/*   SECTION 1 · SEQUENCE CONSTANTS                                */
/* ============================================================== */

export const SEQUENCE_NAME = "PatientListV4" as const;
export const CARD_NAME = "CardPatientListV4";

export const ACTIONS = {
  SEARCH: "SEARCH",
  RESET: "RESET",
  /* ปุ่ม "ส่งต่อ" ของขั้น IN_EXAM -> เลื่อนสถานะไปขั้นถัดไปตามคำสั่งที่เหลือ (#86606) */
  NEXT_STAGE: "NEXT_STAGE",
  /* #86727: Communication Note = DPO sensitive_note (ต่อ EMR/Encounter) */
  LOAD_NOTES: "LOAD_NOTES",
  SAVE_NOTE: "SAVE_NOTE",
} as const;

/** key ใน state.buttonLoadCheck เช่น CardPatientListV4_SEARCH */
export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/* ============================================================== */
/*   SECTION 2 · re-export ของ QUEUE DOMAIN (ไฟล์ pure              */
/*   common/queueDomainV4.ts) — call site เดิมยัง import จากที่นี่ได้  */
/* ============================================================== */

export {
  APPOINT_FORMAT,
  ORDER_KIND_LABELS,
  ORDER_STATE_LABELS,
  QUEUE_STAGES,
  NormalizeFilter,
  ToBEDate,
  appointText,
  createdText,
  nextStageAfterExam,
  orderGroups,
  orderKindOf,
  stageByKey,
  stageOf,
  toQueueEncounter,
  toQueueRows,
  waitText,
} from "../queueDomainV4";
export type {
  FilterType,
  OrderKind,
  OrderState,
  QueueEncounter,
  QueueStage,
  QueueStageKey,
  RawQueueEncounter,
  UiFilterType,
} from "../queueDomainV4";

/* ============================================================== */
/*   SECTION 3 · SEQUENCE TYPE · State / Event / Data / Action     */
/* ============================================================== */

export type Props = {
  onEvent?: (event: any) => void;
  setProp?: (key: string, value: any) => void;
};

/**
 * slice ของ sequence เก็บเฉพาะข้อมูลของตารางคิว
 * loading/error ไปอยู่ที่ state.buttonLoadCheck["<card>_<ACTION>"] + state.errorMessage["<card>"]
 * (team practice ดู common/sequence/PatientOrdersV4.ts และ sequenceStateV4.ts)
 */
/**
 * หลักฐานการเปลี่ยนสถานะ (audit) ของปุ่ม "ส่งต่อ": ผู้ทำ + เวลา + จากขั้นใดไปขั้นใด
 * backend บันทึกผู้ทำ/เวลาไว้จาก apiToken ของ patch อยู่แล้ว ส่วนนี้เก็บไว้แสดงในคิว
 */
export type QueueTransitionInfo = {
  at: string;
  encounterId: number;
  patientName: string;
  fromLabel: string;
  toLabel: string;
  action: string;
  actorId: number | null;
  actorName: string | null;
};

/**
 * #86727: Communication Note 1 record (backend: ClassifiedPatientHistorySerializer_apps_DPO
 * GET /apis/DPO/sensitive_note/?div=... -> แต่ละ record มี emr ของ encounter นั้น)
 */
export type CommunicationNoteInfo = {
  id?: number;
  emr: number;
  note: string;
  editUser: string;
  editedAt: string;
  createdAt: string;
};

export const communicationNoteFromApi = (item: any): CommunicationNoteInfo | null => {
  const emr = Number(item?.emr);
  if (!Number.isFinite(emr)) return null;
  return {
    id: item?.id,
    emr,
    /* backend ส่ง text เป็น HTML entity (ดู examModelV4:647) */
    note: decodeHtml(item?.sensitive_note ?? ""),
    editUser: textOf(item?.edit_user),
    /* #86728: meta แสดง "{วันที่ เวลา}" แบบไทย เช่น "27/10/69 10:15"
       (createdText = helper เดียวกับคอลัมน์เวลาในตารางคิว) */
    editedAt: createdText(item?.created ?? item?.edited ?? ""),
    createdAt: String(item?.created ?? ""),
  };
};

export type PatientListV4Slice = {
  sequenceIndex: "START" | "Action" | null;
  filter: FilterType | null;
  /** แถวคิวที่ map เป็น QueueEncounter พร้อมใส่ตารางแล้ว */
  encounterList: QueueEncounter[];
  total: number;
  /** ผลเปลี่ยนสถานะล่าสุด (สำเร็จ) -> ข้อความแจ้งเหนือตาราง */
  lastTransition: QueueTransitionInfo | null;
  /** audit log การเปลี่ยนสถานะของ session นี้ (ล่าสุดเรียงท้าย) */
  transitionLog: QueueTransitionInfo[];
  /** ผิดพลาดจาก NEXT_STAGE (ไม่ใช้ errorMessage ของ SEARCH เพราะคนละบริบทข้อความ) */
  transitionError: string | null;
  /** #86727: Communication Note per EMR — key = medical_record id ของแถวคิว */
  notesByEmr: Record<number, CommunicationNoteInfo[]>;
  notesError: string | null;
  /** กำลังสร้าง/บันทึก Note (SAVE_NOTE) -> ปุ่มใน modal ชั่วครู่ */
  notesSaving: boolean;
};

export const emptyPatientListV4Slice = (): PatientListV4Slice => ({
  sequenceIndex: null,
  filter: {},
  encounterList: [],
  total: 0,
  lastTransition: null,
  transitionLog: [],
  transitionError: null,
  notesByEmr: {},
  notesError: null,
  notesSaving: false,
});

/** RunSequence ล้าง slice เหลือแค่ { sequenceIndex: "START" } ชั่วครู่ -> อ่านได้โดยไม่ crash */
export const normalizePatientListV4Slice = (
  slice: Partial<PatientListV4Slice> | null | undefined,
): PatientListV4Slice => {
  const base = emptyPatientListV4Slice();
  if (slice === null || slice === undefined) return base;
  return {
    ...base,
    ...slice,
    filter: slice.filter ?? base.filter,
    encounterList: Array.isArray(slice.encounterList) ? slice.encounterList : base.encounterList,
    lastTransition: slice.lastTransition ?? base.lastTransition,
    transitionLog: Array.isArray(slice.transitionLog) ? slice.transitionLog : base.transitionLog,
    transitionError: slice.transitionError ?? base.transitionError,
    notesByEmr: slice.notesByEmr ?? base.notesByEmr,
    notesError: slice.notesError ?? base.notesError,
    notesSaving: slice.notesSaving ?? base.notesSaving,
  };
};

/** state ของ sequence (Partial เพราะ framework เขียนค่าชั่วคราว { sequenceIndex: "START" } ก่อน START) */
export type State = Partial<{
  PatientListV4Sequence: Partial<PatientListV4Slice> | null;
}>;

export const StateInitial: State = {
  PatientListV4Sequence: emptyPatientListV4Slice(),
};

export type Event = { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  division?: number;
  device?: number;
};

export const DataInitial: Data = {};

/* ---- action ที่ส่งเข้ามาผ่าน RunSequence ---- */

type SequenceName = typeof SEQUENCE_NAME;
type Actions = typeof ACTIONS;
export type ActionName = Actions[keyof Actions];

export type SeqState = BaseSeqState<SequenceName> & UiFilterType & { card?: string };

export type ActionPayloadMap = {
  [ACTIONS.SEARCH]: UiFilterType & { card?: string };
  [ACTIONS.RESET]: { card?: string };
  [ACTIONS.NEXT_STAGE]: {
    card?: string;
    encounterId?: number;
    patientName?: string;
    /** คำสั่งของ encounter ที่กดส่งต่อ (ไม่ส่ง -> ใช้แถวในคิว state) */
    doctorOrders?: { specific_type?: string; order_status?: string }[];
    /** ผู้ทำ (audit) จาก django.user ของ MainHIS */
    actorId?: number | null;
    actorName?: string | null;
  };
  [ACTIONS.LOAD_NOTES]: { card?: string };
  [ACTIONS.SAVE_NOTE]: { card?: string; emrId: number; note: string };
};

type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

/** action object ที่ SequencePattern ส่งเข้ามาใน Action */
type ActionType = ActionTypes["ActionType"];

/** function signature ที่ main app ต้อง support (MainHIS supply this.runSequence) */
export type RunSequence = ActionTypes["RunSequence"];

/** params ที่ CardPatientListV4 ต้องส่งให้ runSequence */
export type PatientListRunParams = {
  [K in ActionName]: {
    sequence: typeof SEQUENCE_NAME;
    action: K;
    card?: string;
    restart?: boolean;
    clear?: boolean;
  } & ActionPayloadMap[K];
}[ActionName];

/** type ของ prop runSequence ที่ MainHISV4 ส่งลงมา */
export type PatientListRunSequenceProp = (params: PatientListRunParams) => void;

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type ActionParams<A extends ActionName> = ActionTypes["Params"][A];

type CombinedState = PickedState & State;

export type Handler<P = unknown, R = void> = ActionHandler<CombinedState, Event, Data, P, R>;

type Ctl = {
  getState: () => CombinedState & Record<string, any>;
  setState: (state: any, callback?: () => void) => any;
  handleEvent: (event: Event) => any;
  apiToken: string;
  data: Data;
};

/** meta ของ action ที่กำลังจัดการ (card คือ CardPatientListV4 defaults) */
type ActionMeta = { card?: string; action?: string };

const metaOf = (meta: ActionMeta): { sequence: string; card: string; action?: string } => ({
  sequence: SEQUENCE_NAME,
  card: meta.card ?? CARD_NAME,
  action: meta.action,
});

/**
 * อ่าน error จาก tuple ของ issara-sdk -> ข้อความ
 *  สำเร็จ tuple = [responseData, null, axios response]  <- ช่อง 3 เป็น response object ไม่ใช่ network flag
 *  (รายละเอียดดู common/errorTextV4.ts SECTION 2)
 */
const errorMessageOf = (tuple: [unknown, unknown, unknown]): string | null =>
  apiErrorTextOf(tuple, { requireData: true });

const toNumber = (value: unknown): number | undefined => {
  const num = typeof value === "number" ? value : parseInt(`${value ?? ""}`.trim(), 10);
  return Number.isFinite(num) ? num : undefined;
};

const textOf = (value: unknown): string => `${value ?? ""}`.trim();

/**
 * filter -> params ของ encounter-patient-optimized
 * DRF ไม่ชอบค่าว่าง "" (division/doctor ต้องเป็น id ตัวเลข) จึงไม่ส่ง key ที่ว่าง
 * division ไม่ได้มาจาก UI (หรือไม่ใช่ตัวเลข) -> ใช้ division ของ login ปัจจุบัน
 */
export const queueApiParams = ({
  filter,
  data,
}: {
  filter: FilterType;
  data?: Data;
}): Record<string, unknown> => {
  const division = toNumber(filter.division) ?? toNumber(data?.division);
  const doctor = toNumber(filter.doctor);
  const hn = textOf(filter.patient__hn);
  const patientName = textOf(filter.patient_name);
  const firstName = textOf(filter.first_name);
  const lastName = textOf(filter.last_name);

  return {
    from_date: filter.from_date,
    to_date: filter.to_date,
    exclude_canceled: true,
    ...(division !== undefined && { division }),
    ...(doctor !== undefined && { doctor }),
    ...(hn !== "" && { patient__hn: hn }),
    ...(patientName !== "" && { patient_name: patientName }),
    ...(firstName !== "" && { first_name: firstName }),
    ...(lastName !== "" && { last_name: lastName }),
  };
};

const sliceOf = (controller: Ctl): PatientListV4Slice =>
  normalizePatientListV4Slice(controller.getState().PatientListV4Sequence);

const writeSlice = (
  controller: Ctl,
  meta: ActionMeta,
  patch: SlicePatch<PatientListV4Slice>,
): any =>
  writeSequenceState<PatientListV4Slice>(
    controller,
    metaOf(meta),
    "PatientListV4Sequence",
    sliceOf(controller),
    patch,
    emptyPatientListV4Slice,
  );

const setBtn = (controller: Ctl, meta: ActionMeta, status: BLC_STATUS_TYPE): void =>
  markButtonLoadCheck(controller, [metaOf(meta)], status);

/** export ไว้ให้ test/UI ตรวจ key ที่ใช้ */
export { seqBtnKey as patientListBtnKey };

/* ============================================================== */
/*   SECTION 4 · SEQUENCE HANDLERS · START / ACTION / SEARCH        */
/* ============================================================== */

/* ---- entry point ของ sequence ---- */
export const Start: Handler<SeqState> = async (controller, params) => {
  const filter = NormalizeFilter({ filters: params ?? {} });

  writeSlice(
    controller,
    { card: params.card, action: ACTIONS.SEARCH },
    {
      sequenceIndex: "Action",
      filter,
      encounterList: sliceOf(controller).encounterList,
      loading: true,
    },
  );

  return Action(controller, { ...params, ...filter, action: ACTIONS.SEARCH } as ActionType);
};

/* ---- dispatch ตาม action ที่ส่งมา (map แบบเดียวกับ PatientOrdersV4) ---- */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionName]: Handler<ActionParams<K>> }> = {
    [ACTIONS.SEARCH]: SearchEncounterPatientList,
    [ACTIONS.RESET]: ResetQueue,
    [ACTIONS.NEXT_STAGE]: NextStageAfterExam,
    [ACTIONS.LOAD_NOTES]: LoadCommunicationNotes,
    [ACTIONS.SAVE_NOTE]: SaveCommunicationNote,
  };

  const { action } = params;
  const card = (params as { card?: string }).card;
  const handler = actionHandlers[action as ActionName] as
    Handler<ActionParams<ActionName>> | undefined;

  if (handler) {
    return handler(controller, {
      ...params,
      action: action as ActionName,
      card,
    } as ActionParams<ActionName>);
  }

  console.error(`No handler found for action: ${action}`);

  return writeSlice(
    controller,
    { card, action },
    { error: `ไม่รู้จัก action "${action}" ใน ${SEQUENCE_NAME}` },
  );
};

/** RESET = ล้างตารางคิวรอการค้นหาใหม่ (ไม่ได้เรียก API) */
const ResetQueue: Handler<ActionParams<typeof ACTIONS.RESET>> = async (controller, params) => {
  writeSlice(
    controller,
    { card: params.card, action: ACTIONS.RESET },
    {
      sequenceIndex: "START",
      encounterList: [],
      total: 0,
      error: null,
    },
  );
};

/**
 * เรียก GET /apis/core/encounter-patient-optimized/
 * ?from_date=28/10/2569&to_date=28/10/2569&division=1&exclude_canceled=true
 * (+ patient__hn / patient_name / first_name / last_name / doctor เฉพาะที่มีข้อมูล)
 * วันที่เป็นปี พ.ศ. แล้ว map result -> state.PatientListV4Sequence.encounterList (ใส่ตาราง)
 */
const SearchEncounterPatientList: Handler<ActionParams<typeof ACTIONS.SEARCH>> = async (
  controller,
  params,
) => {
  const meta: ActionMeta = { card: params.card, action: ACTIONS.SEARCH };
  const filter = NormalizeFilter({ filters: params });

  writeSlice(controller, meta, { sequenceIndex: "Action", filter, loading: true, error: null });

  const [response, error, axiosResult] = await EncounterPatientListOptimized.get({
    apiToken: controller.apiToken,
    params: queueApiParams({ filter, data: controller.data }),
    extra: {
      division: controller.data?.division,
      device: controller.data?.device,
    },
  });

  const apiError = errorMessageOf([response, error, axiosResult]);

  if (apiError) {
    return writeSlice(controller, meta, {
      sequenceIndex: "Action",
      loading: false,
      error: apiError,
    });
  }

  const encounterList = toQueueRows(response);
  const total =
    toNumber((response as { total?: unknown })?.total) ??
    toNumber((response as { count?: unknown })?.count) ??
    encounterList.length;

  const searchResult = writeSlice(controller, meta, {
    sequenceIndex: "Action",
    encounterList,
    total,
    loading: false,
    error: null,
  });

  /* #86727: อ่าน Communication Note ของแถวใหม่พร้อมคิว (ล้มเหลว -> notesError แยก ไม่ block ตาราง) */
  if (!apiError) {
    await LoadCommunicationNotes(
      controller,
      { card: params.card } as ActionParams<typeof ACTIONS.LOAD_NOTES>,
    );
  }

  return searchResult;
};

/* ============================================================== */
/*   SECTION 4.1 · Communication Note (#86727) · DPO sensitive_note */
/* ============================================================== */

/**
 * อ่าน Communication Note ของแถวในคิวปัจจุบัน:
 * GET /apis/DPO/sensitive_note/?div=<division> -> group records ตาม emr
 * (endpoint เดียวกับ LOAD_SENSITIVE_NOTE ของหน้าตรวจรักษา — อ่านครั้งเดียวทั้งหน้า
 *  แทนที่จะยิง per-row; filter ด้วย emr ที่คิวเท่านั้น เพื่อไม่ให้เก็บ note ไม่เกี่ยวกัน)
 */
const LoadCommunicationNotes: Handler<ActionParams<typeof ACTIONS.LOAD_NOTES>> = async (
  controller,
  params,
) => {
  const meta: ActionMeta = { card: params.card, action: ACTIONS.LOAD_NOTES };
  const slice = sliceOf(controller);
  const emrIds = slice.encounterList
    .map((row) => row.medical_record)
    .filter((emr): emr is number => typeof emr === "number" && Number.isFinite(emr));

  if (emrIds.length === 0) {
    return writeSlice(controller, meta, { notesByEmr: {}, notesError: null });
  }

  const [response, error, axiosResult] = await ClassifiedPatientHistoryList.list({
    params: { div: controller.data?.division },
    apiToken: controller.apiToken,
    extra: { division: controller.data?.division, device: controller.data?.device },
  });

  const apiError = errorMessageOf([response, error, axiosResult]);
  if (apiError) return writeSlice(controller, meta, { notesError: apiError });

  const rawItems: any[] =
    (response as { items?: any[] })?.items ??
    (Array.isArray(response) ? (response as any[]) : []);

  const wanted = new Set(emrIds);
  const notesByEmr: Record<number, CommunicationNoteInfo[]> = {};

  /* #86728: เรียง เก่า -> ใหม่ ตามเวลาบันทึก (created; fallback edited)
     ไม่เชื่อลำดับที่ backend ส่งมา */
  const timeValueOf = (item: any): number => {
    const t = moment(item?.created ?? item?.edited ?? "");
    return t.isValid() ? t.valueOf() : 0;
  };
  rawItems.sort((a, b) => timeValueOf(a) - timeValueOf(b));

  rawItems.forEach((item) => {
    const note = communicationNoteFromApi(item);
    if (!note || !wanted.has(note.emr)) return;
    notesByEmr[note.emr] = [...(notesByEmr[note.emr] ?? []), note];
  });

  return writeSlice(controller, meta, { notesByEmr, notesError: null });
};

/**
 * บันทึก Communication Note ของ encounter (emrId): ทุกครั้ง = "สร้าง record ใหม่"
 * POST /apis/DPO/sensitive_note/ {emr, division, sensitive_note}
 * แล้วอ่าน notesByEmr ใหม่ -> จำนวนบนแถวตารางอัปเดตทันที (#86729)
 */
const SaveCommunicationNote: Handler<ActionParams<typeof ACTIONS.SAVE_NOTE>> = async (
  controller,
  params,
) => {
  const meta: ActionMeta = { card: params.card, action: ACTIONS.SAVE_NOTE };
  const emrId = params.emrId;
  const draft = textOf(params.note);

  if (!emrId || !Number.isFinite(emrId)) {
    return writeSlice(controller, meta, { notesError: "ไม่พบเวชระเบียน (EMR) ของ encounter นี้" });
  }
  if (draft === "") {
    return writeSlice(controller, meta, { notesError: "กรุณาเขียนข้อความ Note ก่อนบันทึก" });
  }

  writeSlice(controller, meta, { notesSaving: true, notesError: null });

  const [response, error, axiosResult] = await ClassifiedPatientHistoryList.create({
    params: {},
    data: {
      emr: emrId,
      division: [controller.data?.division].filter(
        (div): div is number => div !== undefined && Number.isFinite(Number(div)),
      ),
      sensitive_note: draft,
    },
    apiToken: controller.apiToken,
    extra: { division: controller.data?.division, device: controller.data?.device },
  });

  const apiError = errorMessageOf([response, error, axiosResult]);
  if (apiError) {
    setBtn(controller, meta, BLC_STATUS.ERROR);
    return writeSlice(controller, meta, {
      notesSaving: false,
      notesError: `บันทึก Communication Note ไม่สำเร็จ: ${apiError}`,
    });
  }

  /* อ่านกลับทั้ง list -> notesByEmr/จำนวนบนแถว update ทันที (ข้อ 4 ของ ticket) แล้วปิด flag saving */
  await LoadCommunicationNotes(controller, {
    ...(params as Record<string, unknown>),
    action: ACTIONS.LOAD_NOTES,
  } as ActionParams<typeof ACTIONS.LOAD_NOTES>);

  return writeSlice(controller, meta, { notesSaving: false });
};

/** key ของ buttonLoadCheck ต่อ action (ให้ UI/test ใช้ตรวจได้) */
export const patientListActionKey = (action: ActionName, card: string = CARD_NAME): string =>
  seqBtnKey({ sequence: SEQUENCE_NAME, card, action });

/**
 * ปุ่ม "ส่งต่อ" ของขั้น IN_EXAM (กำลังพบแพทย์): ไม่เปลี่ยนหน้า แค่ patch สถานะ
 * EncounterActionOnly(update-status) ด้วย OPD action ของขั้นถัดไปตามคำสั่งที่เหลือ
 * (nextStageAfterExam ใน queueDomainV4) แล้วค้นหาด้วย filter เดิมเพื่ออัปเดต
 * แถว + จำนวน chip + บันทึก audit (ผู้ทำ/เวลา) ลง lastTransition/transitionLog
 */
const NextStageAfterExam: Handler<ActionParams<typeof ACTIONS.NEXT_STAGE>> = async (
  controller,
  params,
) => {
  const meta: ActionMeta = { card: params.card, action: ACTIONS.NEXT_STAGE };
  const slice = sliceOf(controller);
  const row = slice.encounterList.find((item) => item.id === params.encounterId);
  const encounterId = params.encounterId ?? row?.id;

  if (encounterId === undefined || encounterId === null) {
    return writeSlice(controller, meta, {
      transitionError: "ไม่พบแถวในคิวที่ต้องการเปลี่ยนสถานะ กรุณาค้นหาใหม่",
    });
  }

  const decision = nextStageAfterExam(params.doctorOrders ?? row?.doctor_orders ?? []);
  const fromLabel = stageOf(row)?.label ?? "";

  writeSlice(controller, meta, { loading: true });

  const [response, error, axiosResult] = await EncounterActionOnly.patch({
    pk: encounterId,
    data: { action: decision.action },
    apiToken: controller.apiToken,
    extra: { division: controller.data?.division, device: controller.data?.device },
  });
  const apiError = errorMessageOf([response, error, axiosResult]);

  if (apiError) {
    setBtn(controller, meta, BLC_STATUS.ERROR);
    return writeSlice(controller, meta, {
      transitionError: `เปลี่ยนสถานะไม่สำเร็จ: ${apiError}`,
    });
  }

  /* อ่านคิวด้วย filter เดิม -> สถานะ/จำนวน chip ตรงกับเซิร์ฟเวอร์ */
  await SearchEncounterPatientList(controller, {
    ...(slice.filter ?? {}),
    card: params.card,
  } as ActionParams<typeof ACTIONS.SEARCH>);

  const info: QueueTransitionInfo = {
    at: new Date().toISOString(),
    encounterId,
    patientName: params.patientName ?? row?.patient_name ?? "",
    fromLabel,
    toLabel: decision.stage.label,
    action: decision.action,
    actorId: params.actorId ?? null,
    actorName: params.actorName ?? null,
  };

  return writeSlice(controller, meta, {
    lastTransition: info,
    transitionLog: [...sliceOf(controller).transitionLog, info].slice(-50),
    transitionError: null,
    loading: false,
  });
};
